import type { ReactNode } from 'react';
import { TASK_TYPE_CONFIG } from '../lib/taskTypeConfig';
import type { StatusTone, TaskState, TaskType } from '../lib/types';
import type { PodState } from '../lib/pod';

/**
 * Width-locked task badge — `w-24` keeps every pill the same rendered
 * width so the location column starts at the same X on every row.
 */
export function TaskTypeBadge({
  type,
  label,
  className = '',
}: {
  type: TaskType;
  /** The task's own type label ("Wash"); the bucket's when omitted. */
  label?: string;
  className?: string;
}) {
  const cfg = TASK_TYPE_CONFIG[type];
  const Icon = cfg.icon;
  const text = label ?? cfg.label;
  return (
    <span
      title={text}
      className={
        'inline-flex w-24 shrink-0 items-center justify-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ' +
        cfg.tone +
        ' ' +
        className
      }
    >
      <Icon className="h-3 w-3 shrink-0" />
      <span className="truncate">{text}</span>
    </span>
  );
}

/** Square ISO-3166 country chip — reads as a code chip, not another status pill. */
export function CountryBadge({ code, className = '' }: { code: string; className?: string }) {
  return (
    <span
      title={code}
      className={
        'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-sm border border-slate-300 bg-slate-50 font-mono text-[10px] font-semibold uppercase tracking-tight text-slate-700 ' +
        className
      }
    >
      {code}
    </span>
  );
}

/**
 * One inline data column on a task row. `label` is optional: on the
 * second row of a 2PT shipment the label was already printed above,
 * so we render value-only.
 */
export function DataCell({ label, value }: { label?: string; value: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      {label != null && (
        <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
          {label}
        </span>
      )}
      <div className="truncate text-xs text-slate-700">{value}</div>
    </div>
  );
}

/**
 * Status chip. Tones: red = required / blocking, amber = pending /
 * in progress, green = done / cleared, blue = informational.
 */
export function StatusPill({ tone, label }: { tone: StatusTone; label: string }) {
  const toneClasses: Record<StatusTone, string> = {
    red: 'bg-red-100 text-red-700',
    amber: 'bg-amber-100 text-amber-700',
    green: 'bg-green-100 text-green-700',
    blue: 'bg-blue-100 text-blue-700',
  };
  return (
    <span
      className={
        'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ' +
        toneClasses[tone]
      }
    >
      {label}
    </span>
  );
}

/** POD status mapped off task state. */
export function podPill(state: TaskState, pod: PodState | null = null): ReactNode {
  // A POD on file outranks the leg's execution state: a scan attached before
  // the driver closed the job is still proof, and the strip saying "Required"
  // over a document the operator can see in the panel beside it is the bug this
  // argument exists to fix. "Uploaded", not "Signed" — nobody signed anything.
  if (pod === 'signed') return <StatusPill tone="green" label="Signed" />;
  if (pod === 'uploaded') return <StatusPill tone="green" label="Uploaded" />;
  if (state === 'Completed') return <StatusPill tone="green" label="Signed" />;
  if (state === 'InProgress') return <StatusPill tone="amber" label="Pending" />;
  return <StatusPill tone="red" label="Required" />;
}

/** Customs clearance status mapped off task state. Same traffic-light grammar as podPill. */
export function clearancePill(state: TaskState): ReactNode {
  if (state === 'Completed') return <StatusPill tone="green" label="Cleared" />;
  if (state === 'InProgress') return <StatusPill tone="amber" label="Pending" />;
  return <StatusPill tone="red" label="Required" />;
}

/** Wrapping pill row for the Tags column on cargo tasks. */
export function DataTagList({ tags }: { tags: string[] }) {
  return (
    <div className="flex flex-wrap items-center gap-1">
      {tags.map((tag) => (
        <span
          key={tag}
          className="inline-flex items-center rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700"
        >
          {tag}
        </span>
      ))}
    </div>
  );
}

/**
 * Coloured pill for the task's execution state, rendered next to the
 * STATUS eyebrow in the expanded form.
 */
export function StatusBadge({ state, text }: { state: TaskState; text: string }) {
  const tone =
    state === 'Completed'
      ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
      : state === 'InProgress'
        ? 'border-amber-300 bg-amber-50 text-amber-800'
        : 'border-slate-300 bg-slate-50 text-slate-600';
  return (
    <span
      className={
        'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ' + tone
      }
    >
      {text}
    </span>
  );
}
